import {
  ApiError,
  ApiLog,
  CertificationActionHistory,
  GroupedLogs,
  ThreatReviewUrlReportActionHistory,
} from "@/types";
import { clsx, type ClassValue } from "clsx";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
} from "date-fns";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function shortenString(value: string, prefixNumber: number, suffixNumber: number): string {
  if (!value || prefixNumber < 0 || suffixNumber < 0) return value || "";
  if (value.length <= prefixNumber + suffixNumber) return value;

  // Handle edge cases with early returns
  if (prefixNumber === 0 && suffixNumber === 0) return "...";
  if (prefixNumber === 0) return `...${value.slice(-suffixNumber)}`;
  if (suffixNumber === 0) return `${value.slice(0, prefixNumber)}...`;

  // One-liner regex replacement
  return value.replace(new RegExp(`^(.{${prefixNumber}}).*(.{${suffixNumber}})$`), "$1...$2");
}

export function updateUrlParamsWithLng(newQueryParams: string): string {
  const params = new URLSearchParams(window.location.search);

  // 1. Preserve lng parameter if it exists
  const currentLng = params.get("lng");
  const updatedParams = new URLSearchParams();

  if (currentLng) {
    updatedParams.set("lng", currentLng.trim());
  }

  // 2. Parse new query params and add them (this handles duplicates automatically)
  const newParams = new URLSearchParams(newQueryParams.trim());

  // 3. Add all new params, trimming string values and handling duplicates
  newParams.forEach((value, key) => {
    updatedParams.set(key, value.trim());
  });

  return updatedParams.toString();
}

export function combineWithRandomCharacters(plaintext: string) {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let randomChars = "";
  for (let i = 0; i < 5; i++) {
    randomChars += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return plaintext + randomChars;
}

export function getErrorCode(error: unknown): string | undefined {
  if (!error) return undefined;

  // ApiError emitted by your fetcher
  if (error instanceof ApiError) return error.error_code;

  // axios-like errors (if you ever use axios)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyErr = error as any;
  if (anyErr?.response?.data?.error_code) return anyErr.response.data.error_code;
  if (anyErr?.response?.data?.error_code === null && anyErr?.response?.data?.error)
    return anyErr.response.data.error;

  // fallback: top-level property
  return anyErr?.error_code ?? anyErr?.code;
}

// Check if URL has protocol
export function hasProtocol(url: string) {
  const regex = /^(http|https):\/\//;
  return regex.test(url);
}

export function generateRandomString(length: number) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => ("0" + (byte & 0xff).toString(16)).slice(-2)).join("");
}

export function base64URLEncode(buffer: Buffer) {
  return buffer.toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

export function isJwtExpired(token: string) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

type MenuItem = {
  title: string;
  url: string;
  icon?: any;
  iconActive?: any;
  subItems?: MenuItem[];
};

export const filterMenuByAllowedScreens = (
  items: MenuItem[],
  allowedScreens: string[]
): MenuItem[] => {
  return items
    .map(item => {
      // filter sub items
      const filteredSubItems = item.subItems?.filter(sub => allowedScreens.includes(sub.url));

      const isItemAllowed = allowedScreens.includes(item.url);

      // keep item only if allowed or has allowed sub items
      if (!isItemAllowed && (!filteredSubItems || filteredSubItems.length === 0)) {
        return null;
      }

      return {
        ...item,
        ...(filteredSubItems ? { subItems: filteredSubItems } : {}),
      };
    })
    .filter(Boolean) as MenuItem[];
};

export function formatDate(value: string | Date) {
  const date = new Date(value);
  const now = new Date();

  const seconds = differenceInSeconds(now, date);
  const minutes = differenceInMinutes(now, date);
  const hours = differenceInHours(now, date);
  const days = differenceInDays(now, date);

  if (seconds < 60) {
    return "Just now";
  }

  if (minutes < 60) {
    return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  }

  if (hours < 24) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }

  if (days <= 7) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }

  return format(date, "MMM dd, yyyy hh:mm a");
}
export function transformLogs(data: ApiLog[]): GroupedLogs[] {
  const groupedByDate: Record<string, GroupedLogs> = {};

  data.forEach(item => {
    const dateObj = new Date(item.timestamp);

    const dateKey = format(dateObj, "MMM dd, yyyy");
    const timeKey = dateObj.toISOString(); // 🔥 group by exact timestamp

    if (!groupedByDate[dateKey]) {
      groupedByDate[dateKey] = {
        timestamp: dateKey,
        actions: [],
      };
    }

    const group = groupedByDate[dateKey];

    // 🔥 find existing same timestamp group
    let existing = group.actions.find(a => a._timeKey === timeKey);

    if (!existing) {
      existing = {
        time: format(dateObj, "hh:mm a"),
        action_name: item.action,
        username: item.username,
        notes: item.notes,
        old_value: item.old_value,
        new_value: item.new_value,

        // 🔥 extra fields for grouping
        _timeKey: timeKey,
        _rawTimestamp: dateObj.getTime(),

        // 🔥 aggregated fields
        summary: "",
        key_analysis_factors: "",
        finalized_score: "",
        status: "",
      };

      group.actions.push(existing);
    }

    // 🔥 merge data into same item
    switch (item.action) {
      case "summary_updated":
        existing.summary = item.new_value ?? "";
        break;

      case "key_analysis_factors_updated":
        existing.key_analysis_factors = item.new_value ?? "";
        break;

      case "finalized_score_updated":
        existing.finalized_score = item.new_value ?? "";
        break;

      case "status_changed":
        existing.status = item.new_value ?? "";
        break;

      case "submitted_for_approval":
        existing.action_name = "submitted_for_approval";
        existing.notes = item.notes;
        break;

      case "created_by_bulk_import":
      case "updated_by_bulk_import":
      case "auto_updated_from_appeal":
        existing.summary = item.summary ?? "";
        existing.key_analysis_factors = item.key_analysis_factors ?? "";
        existing.finalized_score = item.finalized_score ?? "";
        existing.status = item.finalized_status ?? "";
        break;

      default:
        // keep original
        break;
    }
  });

  // 🔥 sort actions inside each date
  Object.values(groupedByDate).forEach(group => {
    group.actions.sort((a, b) => b._rawTimestamp - a._rawTimestamp);
  });

  // 🔥 sort dates
  return Object.values(groupedByDate).sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

export function formatActionName(action: string) {
  return action
    .replace("_updated", " Updated")
    .split("_")
    .map(w => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export function downloadCSV(data: any[], filename = "error_logs.csv") {
  if (!data.length) return;

  // Get headers
  const headers = Object.keys(data[0]);

  // Convert to CSV rows
  const rows = data.map(row =>
    headers
      .map(field => {
        const value = row[field] ?? "";
        // Escape quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",")
  );

  const csvContent = [headers.join(","), ...rows].join("\n");

  // Create blob & download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}

export function downloadFileFromUrl(url: string, filename = "error_logs.csv") {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename; // 👈 force download name
  link.target = "_blank"; // optional (helps in some browsers)
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Extract client IP from request headers
 * Works with various proxies: Vercel, Cloudflare, Nginx, AWS, etc.
 * Priority order:
 * 1. X-Forwarded-For (first IP in chain - original client)
 * 2. X-Real-IP (Nginx)
 * 3. CF-Connecting-IP (Cloudflare)
 * 4. True-Client-IP (Cloudflare Enterprise, Akamai)
 * 5. X-Client-IP (General proxy)
 * 6. X-Cluster-Client-IP (GCP, Alibaba)
 * 7. Fastly-Client-IP (Fastly)
 * 8. X-Forwarded (General)
 * 9. Forwarded-For-Ip (General)
 * 10. Forwarded-For (General)
 * 11. X-Appengine-User-IP (GCP App Engine)
 */
export function getClientIp(request: Request): string {
  // Check headers in order of priority
  const headers = [
    "x-forwarded-for",
    "x-real-ip",
    "cf-connecting-ip",
    "true-client-ip",
    "x-client-ip",
    "x-cluster-client-ip",
    "fastly-client-ip",
    "x-forwarded",
    "forwarded-for-ip",
    "forwarded-for",
    "x-appengine-user-ip",
  ];

  for (const header of headers) {
    const value = request.headers.get(header);
    if (value) {
      // X-Forwarded-For can contain multiple IPs: "client, proxy1, proxy2"
      // We want the first one (original client)
      const ip = value.split(",")[0].trim();
      // Validate it looks like an IP (IPv4 or IPv6)
      if (isValidIp(ip)) {
        return ip;
      }
    }
  }

  return "unknown";
}

/**
 * Check if a string is a valid IPv4 or IPv6 address
 */
function isValidIp(ip: string): boolean {
  // IPv4 regex
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  // IPv6 regex (simplified)
  const ipv6Regex =
    /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::$|^([0-9a-fA-F]{1,4}:){1,7}:$|^:([0-9a-fA-F]{1,4}:){1,7}$|^([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}$/;

  if (ipv4Regex.test(ip)) {
    // Validate each octet is 0-255
    const parts = ip.split(".");
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  }

  return ipv6Regex.test(ip) || ip === "::1"; // ::1 is localhost IPv6
}
